import { LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoginNavbar from '../components/auth/LoginNavbar';
import { useAccess } from '../components/access';

export default function RequestAccess() {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const { access, isLoading: isAccessLoading } = useAccess();
  const navigate = useNavigate();

  if (isLoading || isAccessLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  // Prevent users from accessing this page if they should not see it
  if (isAuthenticated && access?.role !== 'user:waitlist') {
    navigate('/overview', { replace: true });
    return null;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to sign out:', error);
    } finally {
      // Always navigate to login regardless of logout success/failure
      navigate('/login');
    }
  };

  return (
    <>
      <LoginNavbar />
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#FEF0E6] to-white dark:from-gray-900 dark:to-gray-800 px-4 py-12">
        <img src="/assets/jua-logo.svg" alt="Jua" className="h-16 w-auto mb-8 dark:invert" />
        <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-4 text-center">
          Access Temporarily Paused
        </h1>
        <p className="max-w-xl text-lg text-gray-700 dark:text-gray-300 mb-8 text-center">
          Your account has been successfully set up, but due to exceptionally high demand, we are
          temporarily pausing new user access.
        </p>
        <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl p-8 mb-8 max-w-lg w-full">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            What happens next?
          </h2>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E34290]/10 dark:bg-[#E34290]/10 flex items-center justify-center mr-3">
                <span className="h-3 w-3 rounded-full bg-[#E34290]"></span>
              </span>
              <span className="text-gray-800 dark:text-gray-200 text-base">
                As soon as we are able to onboard more users, your access will be enabled
                automatically.
              </span>
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#E34290]/10 dark:bg-[#E34290]/10 flex items-center justify-center mr-3">
                <span className="h-3 w-3 rounded-full bg-[#E34290]"></span>
              </span>
              <span className="text-gray-800 dark:text-gray-200 text-base">
                You will receive an email notification as soon as your access is activated, no
                further action is required on your part.
              </span>
            </li>
          </ul>
        </div>
        <div className="text-center max-w-xl">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            We appreciate your patience and understanding as we work to accommodate all our users.
            If you have any urgent questions, please feel free to contact our support team at
            <a href="mailto:support@jua.ai" className="text-[#E34290] hover:underline ml-1">
              support@jua.ai
            </a>
            .
          </p>
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-[#E34290] bg-white dark:bg-gray-800 border border-[#E34290] rounded-md hover:bg-[#E34290]/5 dark:hover:bg-[#E34290]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E34290] dark:focus:ring-offset-gray-900 transition-colors duration-200"
            data-ph-capture-attribute="btn-request-access-return-to-login"
          >
            <LogIn className="w-5 h-5 mr-2" />
            {isAuthenticated ? 'Logout' : 'Return to Login'}
          </button>
        </div>
      </div>
    </>
  );
}
